import { Shield, FileText, CreditCard, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    documentSubtype: 'PRE_AUTH_REQUEST' | 'CLAIM_PACK' | 'CLAIM_RECEIPT' | 'APPROVAL_LETTER' | 'PATIENT_ESTIMATE' | 'INSURER_ESTIMATE' | 'HOSPITAL_ESTIMATE';
    policyId: string;
    policyHolder?: string;
    insurerName: string;
    insurerNhcId?: string;
    tpaName?: string;
    tpaId?: string;
    preAuthId?: string;
    claimId?: string;
    hospitalName?: string;
    hospitalNhcId?: string;
    admissionDate?: string;
    dischargeDate?: string;
    diagnosis?: string;
    procedureName?: string;
    estimateAmount?: number;
    approvedAmount?: number;
    rejectedAmount?: number;
    patientPayable?: number;
    insurerPayable?: number;
    deductible?: number;
    copay?: number;
    approvalStatus?: string;
    rejectionReason?: string;
    documentsList?: string[];
    notes?: string[];
    signatories: { name: string; role: string; nhcId?: string; licenseNo?: string; institution?: string }[];
  };
}

function fmt(n: number) { return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`; }

const SUBTYPE_LABELS: Record<string, string> = {
  PRE_AUTH_REQUEST: 'Insurance Pre-Authorization Request',
  CLAIM_PACK: 'Insurance Claim Document Pack',
  CLAIM_RECEIPT: 'Insurance Claim Receipt',
  APPROVAL_LETTER: 'Claim Approval/Rejection Letter',
  PATIENT_ESTIMATE: 'Patient Payable Estimate',
  INSURER_ESTIMATE: 'Insurer Payable Estimate',
  HOSPITAL_ESTIMATE: 'Hospital Estimate for Insurance',
};

export default function InsuranceDocumentTemplate({ data }: Props) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <Shield className="w-3.5 h-3.5 text-gray-700" />
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-700">
          {SUBTYPE_LABELS[data.documentSubtype] || 'Insurance Document'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <div><span className="text-gray-500">Policy ID:</span> <span className="font-mono text-gray-800">{data.policyId}</span></div>
        <div><span className="text-gray-500">Insurer:</span> <span className="text-gray-800 font-medium">{data.insurerName}</span></div>
        {data.policyHolder && <div><span className="text-gray-500">Holder:</span> <span className="text-gray-800">{data.policyHolder}</span></div>}
        {data.tpaName && <div><span className="text-gray-500">TPA:</span> <span className="text-gray-800">{data.tpaName}</span></div>}
        {data.preAuthId && <div><span className="text-gray-500">Pre-Auth ID:</span> <span className="font-mono text-gray-800">{data.preAuthId}</span></div>}
        {data.claimId && <div><span className="text-gray-500">Claim ID:</span> <span className="font-mono text-gray-800">{data.claimId}</span></div>}
        {data.hospitalName && <div><span className="text-gray-500">Hospital:</span> <span className="text-gray-800">{data.hospitalName}</span></div>}
        {data.admissionDate && <div><span className="text-gray-500">Admission:</span> <span className="text-gray-800">{data.admissionDate}</span></div>}
        {data.dischargeDate && <div><span className="text-gray-500">Discharge:</span> <span className="text-gray-800">{data.dischargeDate}</span></div>}
      </div>

      {data.diagnosis && (
        <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px]">
          <span className="text-gray-500">Diagnosis:</span> <span className="text-gray-800">{data.diagnosis}</span>
          {data.procedureName && <span className="text-gray-600 ml-2">· Procedure: {data.procedureName}</span>}
        </div>
      )}

      {/* Approval Status */}
      {data.approvalStatus && (
        <div className={`px-3 py-1.5 border rounded text-[9px] flex items-center gap-1.5 ${
          data.approvalStatus === 'APPROVED' ? 'bg-green-50 border-green-200 text-green-800' :
          data.approvalStatus === 'PARTIAL' ? 'bg-amber-50 border-amber-200 text-amber-800' :
          data.approvalStatus === 'REJECTED' ? 'bg-red-50 border-red-200 text-red-800' :
          'bg-blue-50 border-blue-200 text-blue-800'
        }`}>
          {data.approvalStatus === 'APPROVED' ? <CheckCircle className="w-3 h-3" /> :
           data.approvalStatus === 'REJECTED' ? <XCircle className="w-3 h-3" /> :
           <AlertCircle className="w-3 h-3" />}
          <span className="font-bold">{data.approvalStatus}</span>
          {data.rejectionReason && <span className="ml-1">— {data.rejectionReason}</span>}
        </div>
      )}

      {/* Financial Summary */}
      {(data.estimateAmount !== undefined || data.approvedAmount !== undefined) && (
        <div className="flex justify-end">
          <div className="w-60 bg-gray-50 border border-gray-200 rounded text-[9px]">
            {data.estimateAmount !== undefined && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Estimate</span><span className="font-mono text-gray-800">{fmt(data.estimateAmount)}</span></div>}
            {data.approvedAmount !== undefined && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Approved</span><span className="font-mono text-green-700">{fmt(data.approvedAmount)}</span></div>}
            {(data.rejectedAmount ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Rejected</span><span className="font-mono text-red-700">{fmt(data.rejectedAmount!)}</span></div>}
            {(data.deductible ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Deductible</span><span className="font-mono text-gray-800">{fmt(data.deductible!)}</span></div>}
            {(data.copay ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Co-pay</span><span className="font-mono text-gray-800">{fmt(data.copay!)}</span></div>}
            {data.insurerPayable !== undefined && <div className="flex justify-between py-1 px-2.5 bg-blue-50 font-bold border-b border-gray-300"><span className="text-blue-800">Insurer Payable</span><span className="font-mono text-blue-900">{fmt(data.insurerPayable)}</span></div>}
            {data.patientPayable !== undefined && <div className="flex justify-between py-1 px-2.5 bg-gray-100 font-bold"><span className="text-gray-800">Patient Payable</span><span className="font-mono text-gray-900">{fmt(data.patientPayable)}</span></div>}
          </div>
        </div>
      )}

      {data.documentsList && data.documentsList.length > 0 && (
        <div className="text-[9px]">
          <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-0.5 flex items-center gap-1"><FileText className="w-2.5 h-2.5" /> Attached Documents</p>
          <ol className="list-decimal list-inside space-y-0.5 text-gray-700 bg-gray-50 px-3 py-1.5 border border-gray-200 rounded">
            {data.documentsList.map((d, i) => <li key={i}>{d}</li>)}
          </ol>
        </div>
      )}

      {data.documentSubtype === 'APPROVAL_LETTER' && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-[8px] text-amber-800">
          Placeholder — Final approval/rejection letters are issued by the insurance provider or TPA.
        </div>
      )}

      {data.notes && data.notes.length > 0 && (
        <ul className="list-disc list-inside space-y-0.5 text-[9px] text-gray-700 bg-gray-50 px-3 py-1.5 border border-gray-200 rounded">
          {data.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}

      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
